/**
 * @fileoverview API client configuration hooks and components for different user types.
 */

import React, {PropsWithChildren, useEffect} from "react";

import {Config, configureApiClient} from "../services/api";
import {dispatch, select, store} from "../store";
import {AUTH_SIGN_IN_URL} from "../services/AuthService";
import {useClearAndCancelCache} from "../hooks/useClearAndCancelCache";
import {AxiosRequestConfig} from "axios";
import {configureRestApiClient} from "../services/api/restApiClient";

/**
 * Creates API client configuration for admin users.
 *
 * @param {Function} clearAndCancelCache Function to clear and cancel cache.
 * @returns {Config} API client configuration for admin users.
 */
export const createConfig = (clearAndCancelCache: () => void): Config => ({
  getToken: () => select.adminSession.getToken(store.getState()),
  setToken: (token: string) =>
    dispatch.adminSession.setToken({token, isLoginToken: false}),
  logout: () => {
    dispatch.adminSession.logout();
    clearAndCancelCache();
  },
  // auth/sign-in will respond with status UNAUTHORIZED for bad password and login combination
  skipUnauthorizedHandling: (config: AxiosRequestConfig) => {
    return config?.url === AUTH_SIGN_IN_URL;
  },
});

/**
 * Custom hook for configuring the main API client.
 *
 * @param {Config} config API client configuration.
 */
export const useApiClientConfiguration = (config: Config) => {
  useEffect(() => {
    configureApiClient(config);
  }, [config]);
};

/**
 * Custom hook for configuring the REST API client.
 *
 * @param {Config} config API client configuration.
 */
export const useRestApiClientConfiguration = (config: Config) => {
  useEffect(() => {
    configureRestApiClient(config);
  }, [config]);
};

/**
 * Configuration component for admin API client.
 * Sets up API client with admin session configuration.
 *
 * @param {Object} props Component props.
 * @param {React.ReactNode} [props.children] Optional children to render.
 * @returns {JSX.Element} The configuration component.
 */
export const AdminApiClientConfiguration: React.FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const {clearAndCancelCache} = useClearAndCancelCache();
  const config = createConfig(clearAndCancelCache);

  useApiClientConfiguration(config);

  return <div>{children}</div>;
};

/**
 * Configuration component for REST API client.
 * Sets up REST API client with admin session configuration.
 *
 * @param {Object} props Component props.
 * @param {React.ReactNode} [props.children] Optional children to render.
 * @returns {JSX.Element} The configuration component.
 */
export const RestApiClientConfiguration: React.FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const {clearAndCancelCache} = useClearAndCancelCache();
  const config = createConfig(clearAndCancelCache);

  useRestApiClientConfiguration(config);

  return <div>{children}</div>;
};

/**
 * Admission API client config (shared by main API and REST API when on admission routes).
 */
const admissionConfig: Config = {
  getToken: () => select.admissionSession.getToken(store.getState()),
  setToken: dispatch.admissionSession.setToken,
  logout: dispatch.admissionSession.logout,
};

/**
 * Configuration component for admission API client.
 * Sets up API client with admission session configuration.
 *
 * @param {Object} props Component props.
 * @param {React.ReactNode} [props.children] Optional children to render.
 * @returns {JSX.Element} The configuration component.
 */
export const AdmissionApiClientConfiguration: React.FC<
  PropsWithChildren<{}>
> = ({children}) => {
  useApiClientConfiguration(admissionConfig);

  return <div>{children}</div>;
};

/**
 * Configuration component for REST API client on admission routes.
 * Ensures /auth/refresh and other REST calls use admission session and store
 * refreshed token in admissionSession when X-Refreshed-JWT is received.
 *
 * @param {Object} props Component props.
 * @param {React.ReactNode} [props.children] Optional children to render.
 * @returns {JSX.Element} The configuration component.
 */
export const AdmissionRestApiClientConfiguration: React.FC<
  PropsWithChildren<{}>
> = ({children}) => {
  useRestApiClientConfiguration(admissionConfig);

  return <div>{children}</div>;
};
